// ─── Message masqué : MODÉRATION et SUPPRESSION (Core) ────────────
// Vocabulaire fixé par Cédric le 09/08. Trois états, jamais deux à la fois :
//
//   • MODÉRÉ PAR L'AUTEUR   — il ne veut plus l'afficher, sans le détruire.
//     Masqué pour TOUS sauf lui. **Sans retour** (« on ne peut plus le
//     réafficher dans ce cas ») : on reposte si on veut, comme pour la
//     fermeture d'une discussion.
//   • MODÉRÉ PAR L'ADMINISTRATEUR — l'administrateur de la discussion (= son
//     créateur) le retire de la vue des autres. Il ne détruit rien : l'auteur
//     voit son message ET qu'il est modéré, et décide de le supprimer ou non.
//     **Sans retour lui aussi** (Cédric, 09/08 : « pas de réversibilité, car si
//     le modérateur tranche, il a raison »). Ce qui se défait, c'est un
//     SIGNALEMENT — pas une décision.
//   • SUPPRIMÉ — l'AUTEUR seul. Le CONTENU part, **le document reste**.
//
// Pourquoi garder le document d'un message supprimé : son id est référencé
// ailleurs — les réponses qui le citent, l'ordre du fil, les marqueurs de
// lecture. Le détruire laisserait des citations orphelines. C'est une pierre
// tombale : un id, un auteur, une date, plus aucun contenu.
//
// En base :
//   modere   = { par: 'auteur' | 'admin', uid, nom, le }   (absent = net)
//   supprime = { le }                                       + texte/media/position vidés
//
// Un simple flag suffit alors que Firestore n'a pas de sécurité au niveau du
// champ : ceux qui pourraient encore lire le texte via l'API sont DÉJÀ membres
// de la discussion — ils l'avaient sous les yeux avant. C'est un signal social,
// pas un secret. La SUPPRESSION, elle, vide réellement la base.

// L'extension `.js` est OBLIGATOIRE : `node --test` résout en ESM strict et
// refuse un import sans extension. Metro l'accepte aussi — un import nu
// n'aurait cassé qu'au moment des tests, pas à l'export.
import {
  SOURCE_MEMBRE, SOURCE_IA, signalePar, detailsSignalement, signaleParIA,
  signaleParMembre, estSignale, signalementAffichable, entreeSignalement,
} from './signalement.js';

// Réexportés : l'écran n'a qu'un module à connaître pour rendre un message.
export {
  SOURCE_MEMBRE, SOURCE_IA, signalePar, detailsSignalement, signaleParIA,
  signaleParMembre, estSignale, signalementAffichable, entreeSignalement,
};

// L'administrateur d'une discussion est son créateur. Le propriétaire de
// l'espace hérite de l'administration mais JAMAIS de la lecture
// (cf. cibleDiscussion.js), donc jamais de la modération.
export const estAdminDiscussion = (disc, uid) => !!uid && !!disc && disc.creePar === uid;

// Vue d'un message pour un lecteur — une seule fonction, l'écran ne fait que rendre.
//   → { cause, par, visible, role, signale, signaleMembre }
//     cause         : null | 'supprime' | 'modere'
//     par           : 'auteur' | 'admin' | null   (qui a modéré)
//     visible       : le contenu réel est-il rendu ?
//     role          : 'auteur' | 'admin' | 'tiers'
//     signale       : un signalement est-il en cours (humain ou IA) ?
//     signaleMembre : au moins un signalement HUMAIN — l'IA seule n'alerte que
//                     l'administrateur, jamais l'auteur.
export function vueMessage(msg, uid, disc) {
  const estAuteur = !!uid && msg?.auteurUid === uid;
  const admin = estAdminDiscussion(disc, uid);
  const role = estAuteur ? 'auteur' : (admin ? 'admin' : 'tiers');
  const signale = estSignale(msg);
  const sigMembre = signale && signaleParMembre(msg);

  // Supprimé : terminal. Le contenu n'existe plus, personne ne le voit — pas
  // même l'auteur : il l'a détruit en connaissance de cause.
  if (msg?.supprime) return { cause: 'supprime', par: null, visible: false, role, signale: false, signaleMembre: false };

  const par = msg?.modere?.par || null;
  if (!par) return { cause: null, par: null, visible: true, role, signale, signaleMembre: sigMembre };

  // L'auteur voit toujours ce qu'il a écrit tant qu'il ne l'a pas supprimé.
  if (estAuteur) return { cause: 'modere', par, visible: true, role, signale: false, signaleMembre: false };
  // L'administrateur voit ce QU'IL a modéré — pas ce que l'auteur a repris.
  if (par === 'admin' && admin) return { cause: 'modere', par, visible: true, role, signale: false, signaleMembre: false };
  return { cause: 'modere', par, visible: false, role, signale: false, signaleMembre: false };
}

// Clé i18n du libellé affiché à la place du contenu. On renvoie la clé, pas une
// chaîne : la traduction reste dans le Core. Les trois libellés DIFFÈRENT — ils
// ne disent pas la même chose au lecteur.
export function clePlaceholder({ cause, par } = {}) {
  if (cause === 'supprime') return 'messagerie.messageSupprime';
  if (cause !== 'modere') return null;
  return par === 'admin' ? 'messagerie.messageModereAdmin' : 'messagerie.messageModereAuteur';
}

// Citer remettrait le contenu en clair dans le fil, pour ceux à qui on vient de
// le retirer. Donc : seuls les messages nets.
export const citable = (msg) => !msg?.modere && !msg?.supprime;

// Actions offertes, selon le lecteur.
export function actionsMessage(msg, uid, disc) {
  const vue = vueMessage(msg, uid, disc);
  const estAuteur = vue.role === 'auteur';
  const admin = estAdminDiscussion(disc, uid);
  const supprime = !!msg?.supprime;
  const par = msg?.modere?.par || null;
  const dejaSignale = signalePar(msg).includes(uid);
  return {
    ...vue,
    // Rien ne survit à la suppression : c'est l'état terminal.
    supprimer:     estAuteur && !supprime,
    modererAuteur: estAuteur && !supprime && !par,            // sans retour
    modererAdmin:  admin && !estAuteur && !supprime && !par,  // sur le message d'un autre
    // Signaler : tout membre SAUF l'auteur (qui modère ou supprime directement),
    // une seule fois, et seulement sur un message encore net.
    signaler:   !!uid && !estAuteur && !supprime && !par && !dejaSignale,
    // Lever le signalement : l'ADMIN seul — c'est lui qui arbitre. L'auteur ne
    // peut pas faire taire un signalement qui le vise ; il répond en modérant
    // ou en supprimant.
    leverSignalement: admin && vue.signale,
    // PAS de `demodererAdmin` : la modération de l'administrateur est SANS
    // RETOUR (Cédric, 09/08 : « si le modérateur tranche, il a raison »).
    // Absence volontaire — ne pas la « corriger » en croyant à un oubli.
  };
}

// Forme d'un message une fois SUPPRIMÉ — le contenu part vraiment de la base,
// l'identité reste. Utilisé par le service, testé ici.
// ⚠️ APPELANT : le service doit AUSSI supprimer le fichier dans Storage
// (Cédric, 09/08 : « la pièce jointe est supprimée si l'auteur supprime, seul
// le message id est gardé »). Vider `media` ici ne fait que perdre l'URL —
// l'objet resterait facturé et atteignable par quiconque l'avait déjà.
export function versSupprime(msg, iso) {
  return {
    ...msg,
    texte: '', media: null, position: null, repondA: null,
    // Ni modération ni signalement n'ont de sens sur un contenu qui n'existe
    // plus — et les laisser ferait survivre une pastille sans objet.
    modere: null, signale: null,
    supprime: { le: iso },
  };
}

// ─── Rendu : fond du message ──────────────────────────────────────
// Cédric, 09/08 : « le message doit changer de couleur de fond, en rouge pour
// l'auteur et celui qui a modéré ». On renvoie un JETON SÉMANTIQUE, pas une
// couleur : le thème vit dans `useUI()`, et le rouge est déjà la couleur
// d'alerte de la suite (bord rouge des champs en erreur — cf. AGENTS.md).
//
// Progression : orange = à décider, rouge = décidé.
//   'attention' → SIGNALÉ, vu par l'auteur ou l'admin. Orange : une décision
//                 est attendue d'eux deux.
//   'alerte'    → modéré par l'ADMIN, vu par l'auteur ou par l'admin. Rouge :
//                 c'est tranché, l'auteur décide encore de supprimer ou non.
//   'discret'   → modéré par l'AUTEUR, vu par lui. Pas une alerte : c'est son
//                 propre geste, il n'a rien à arbitrer.
//   null        → rien de particulier (message net, placeholder d'un tiers, ou
//                 signalement vu par un tiers — l'arbitrage ne le regarde pas).
export function fondMessage(vue) {
  if (!vue?.visible) return null;
  if (vue.cause === 'modere') return vue.par === 'admin' ? 'alerte' : 'discret';
  if (!vue.signale) return null;
  // L'ADMIN voit tout signalement — humain comme automatique : c'est lui qui
  // tranche, et c'est lui qui aura payé l'IA.
  if (vue.role === 'admin') return 'attention';
  // L'AUTEUR n'est alerté que par un signalement HUMAIN. Un faux positif
  // automatique ne doit pas l'accuser avant qu'un humain n'ait repris la main
  // (Cédric, 09/08 : « le modérateur seulement »).
  if (vue.role === 'auteur' && vue.signaleMembre) return 'attention';
  return null;
}

// ─── Pastille « quelque chose attend ta décision » ────────────────
// Remonte sur la discussion. C'est un ÉTAT, pas une notification : elle reste
// tant que la situation dure et retombe quand elle est tranchée (message
// supprimé, modéré, signalement levé). Elle appelle une action, elle ne signale
// pas un simple passage — donc aucun marqueur « vu » à tenir en plus des
// lectures de messages.
//
// Attend une décision de MOI si :
//   • je suis l'AUTEUR   — mon message est signalé, ou modéré par l'admin ;
//   • je suis l'ADMIN    — un message de la discussion est signalé.
export function attendMaDecision(msg, uid, disc) {
  if (!uid || !msg || msg.supprime) return false;
  const estAuteur = msg.auteurUid === uid;
  if (estAuteur && msg.modere?.par === 'admin') return true;
  if (!estSignale(msg)) return false;
  if (estAdminDiscussion(disc, uid)) return true;          // humain OU IA
  return estAuteur && signaleParMembre(msg);               // humain seulement
}

export const compterAArbitrer = (messages, uid, disc) =>
  (messages || []).filter(m => attendMaDecision(m, uid, disc)).length;

// Aperçu dans la LISTE des discussions (dernier message). L'oubli classique :
// on masque la bulle dans le fil, et le texte ressort en « dernier message »
// sur l'écran d'accueil. Médias et position compris.
export function apercuPour(msg, uid, disc) {
  if (!msg) return null;
  const vue = vueMessage(msg, uid, disc);
  if (vue.visible) return msg;
  return { ...msg, texte: '', media: null, position: null, masque: true, cause: vue.cause, par: vue.par };
}
